import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  usePrimaryComplaintStore,
  ComplaintHistoryStep,
  VisitReason,
  QualityOptions,
} from "@/store/primaryComplaint";
import { complaintHistoryLabels as t } from "./complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { ChoiceGroup } from "@/components/forms/ChoiceGroup";
import { ComplaintStepHeader, StepLoadError } from "./ComplaintStepHeader";
import { useComplaintValues } from "@/hooks/useComplaintValues";
import { missingRequired, resolveComplaintType } from "@/utils/complaintSteps";
import { useTaskStore } from "@/store/task";
import { askVasFor } from "@/types/task-actions";

const Fields = {
  QUALITIES: "Qualities",
  WORK: "Work",
  SLEEP: "Sleep",
  CURRENT_SEVERITY: "CurrentSeverity",
} as const;

const QualityOptionsList = [
  QualityOptions.sharp,
  QualityOptions.dull,
  QualityOptions.aching,
  QualityOptions.throbbing,
  QualityOptions.burning,
  QualityOptions.stabbing,
  QualityOptions.shooting,
  QualityOptions.tingling,
  QualityOptions.Numbness,
];

const range = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => String(from + i)).map((n) => ({ value: n, label: n }));
const SEVERITY_OPTIONS = range(1, 10);
const IMPACT_OPTIONS = range(0, 10);

/** Ratings start unanswered (null) and are only saved once the patient picks one. */
interface Answers {
  CurrentSeverity: string | null;
  Sleep: string | null;
  Work: string | null;
  Qualities: string[];
}

export function ComplaintHistory2() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const complaintIdParam = searchParams.get("complaintId");

  const [answers, setAnswers] = useState<Answers>({ CurrentSeverity: null, Sleep: null, Work: null, Qualities: [] });
  const [isSaving, setIsSaving] = useState(false);
  const [missing, setMissing] = useState<string | null>(null);

  const complaintHeaders = usePrimaryComplaintStore((s) => s.complaintHeaders);
  const visitReason = usePrimaryComplaintStore((s) => s.visitReason);
  const updateComplaintValue = usePrimaryComplaintStore((s) => s.updateComplaintValue);
  const setComplaintHistoryStep = usePrimaryComplaintStore((s) => s.setComplaintHistoryStep);
  const { values, status, retry } = useComplaintValues(complaintIdParam);
  // The case history config can turn the 1-10 rating off; it is then neither asked nor saved.
  const askVas = useTaskStore((s) => askVasFor(s.portalConfigMeta));

  const complaintType = resolveComplaintType(complaintHeaders, complaintIdParam, visitReason);
  const isPainComplaint = complaintType === VisitReason.PAIN;

  useEffect(() => {
    if (status !== "ready" || !values) return;
    const next: Answers = { CurrentSeverity: null, Sleep: null, Work: null, Qualities: [] };
    for (const v of values) {
      const raw = (v.value ?? "").trim();
      if (v.code === Fields.QUALITIES) {
        next.Qualities = raw.split(",").map((q) => q.trim()).filter(Boolean);
      } else if (v.code === Fields.CURRENT_SEVERITY) {
        // Severity is 1–10; a stored 0 is the old slider default, not an answer.
        next.CurrentSeverity = /^(10|[1-9])$/.test(raw) ? raw : null;
      } else if (v.code === Fields.SLEEP || v.code === Fields.WORK) {
        next[v.code] = /^(10|[0-9])$/.test(raw) ? raw : null;
      }
    }
    setAnswers(next);
  }, [status, values]);

  const rate = (field: "CurrentSeverity" | "Sleep" | "Work", value: string | null) => {
    if (value == null) return;
    setAnswers((prev) => ({ ...prev, [field]: value }));
    if (missing === "severity" && field === "CurrentSeverity") setMissing(null);
    void updateComplaintValue(field, value).catch(() => {});
  };

  const selectQualities = (next: string[]) => {
    setAnswers((prev) => ({ ...prev, Qualities: next }));
    if (missing === "quality" && next.length) setMissing(null);
    void updateComplaintValue(Fields.QUALITIES, next.join(",")).catch(() => {});
  };

  const goBack = () => setComplaintHistoryStep(ComplaintHistoryStep.ComplaintHistory1);

  const saveAndContinue = async () => {
    if (status !== "ready") return;
    const firstMissing = missingRequired([
      { id: "severity", answered: !askVas || answers.CurrentSeverity != null },
      { id: "quality", answered: !isPainComplaint || answers.Qualities.length > 0 },
    ]);
    if (firstMissing) {
      setMissing(firstMissing);
      const el = document.getElementById(firstMissing);
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
      el?.focus({ preventScroll: true });
      return;
    }
    // Re-save every answered field and wait; unanswered ratings are never written.
    const saves: Promise<unknown>[] = [];
    for (const field of ["CurrentSeverity", "Sleep", "Work"] as const) {
      if (field === "CurrentSeverity" && !askVas) continue;
      const v = answers[field];
      if (v != null) saves.push(updateComplaintValue(field, v));
    }
    if (answers.Qualities.length) saves.push(updateComplaintValue(Fields.QUALITIES, answers.Qualities.join(",")));
    setIsSaving(true);
    try {
      await Promise.all(saves);
    } catch {
      // updateComplaintValue already shows a toast; stay so the patient can retry.
      setIsSaving(false);
      return;
    }
    setIsSaving(false);
    if (isPainComplaint) {
      setComplaintHistoryStep(ComplaintHistoryStep.ComplaintHistory3);
      return;
    }
    navigate({ pathname: "/complaint/complaint-factors", search: searchParams.toString() });
  };

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content p-4 flex flex-col">
        <div className="complaint-history-card">
          <ComplaintStepHeader complaintId={complaintIdParam} step="history2" />

          {status === "loading" && (
            <div className="wizard-skeleton" role="status" aria-busy="true" aria-label="Loading your answers">
              <span />
              <span />
              <span />
            </div>
          )}

          {status === "error" && <StepLoadError onRetry={retry} />}

          {status === "ready" && (
            <>
              {askVas && (
                <ChoiceGroup
                  id="severity"
                  layout="numbers"
                  required
                  legend={t.severityQuestion}
                  hint={`${t.minimal}, ${t.severe}`}
                  options={SEVERITY_OPTIONS}
                  value={answers.CurrentSeverity}
                  onChange={(v) => rate("CurrentSeverity", v)}
                  error={missing === "severity" ? t.answerToContinue : null}
                />
              )}
              <ChoiceGroup
                id="sleep"
                layout="numbers"
                legend={t.sleepQuestion}
                hint={`${t.noEffect}, ${t.worstEffect}`}
                options={IMPACT_OPTIONS}
                value={answers.Sleep}
                onChange={(v) => rate("Sleep", v)}
              />
              <ChoiceGroup
                id="work"
                layout="numbers"
                legend={t.workQuestion}
                hint={`${t.noEffect}, ${t.worstEffect}`}
                options={IMPACT_OPTIONS}
                value={answers.Work}
                onChange={(v) => rate("Work", v)}
              />

              {isPainComplaint && (
                <ChoiceGroup
                  id="quality"
                  multiple
                  required
                  legend={t.qualityQuestion}
                  hint={`(${t.selectAllApply})`}
                  options={QualityOptionsList.map((q) => ({ value: q, label: t[q as keyof typeof t] }))}
                  value={answers.Qualities}
                  onChange={selectQualities}
                  error={missing === "quality" ? t.answerToContinue : null}
                />
              )}

            </>
          )}
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack} aria-label={t.backButton}>
          ← {t.backButton}
        </Button>
        <Button type="button" onClick={saveAndContinue} disabled={isSaving || status !== "ready"}>
          {isSaving ? "Saving…" : t.continueButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
